import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { isDesktopRuntime } from '@/auth/desktopAuth';

// Global token getter that can be set by the app
let tokenGetter: (() => string | null) | null = null;

// Function to set the token getter (called by app initialization)
export const setTokenGetter = (getter: () => string | null) => {
  tokenGetter = getter;
};

export const getAuthToken = (): string | null => {
  let token: string | null = null;
  if (tokenGetter) {
    token = tokenGetter();
  }
  if (!token) {
    token = localStorage.getItem('edgelex_access_token');
  }
  return token;
};

// Create Axios instance with base configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: '/api/v1', // EdgeLex backend API base URL
  timeout: 10000, // 10 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to automatically add JWT token
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('🔴 [Axios] No JWT token available for request to:', config.url);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for global error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError) => {
    // Handle 401 Unauthorized - redirect to login
    if (error.response?.status === 401) {
      console.warn('Unauthorized request - token may be expired');
      if (!isDesktopRuntime()) {
        // Browser Keycloak sessions are represented in web storage and use
        // the browser login route. Native sessions are main-process owned;
        // redirecting the renderer on every 401 creates an infinite reload.
        localStorage.removeItem('edgelex_access_token');
        localStorage.removeItem('edgelex_refresh_token');
        localStorage.removeItem('edgelexFirmId');
        window.location.href = '/';
      }
    }
    
    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      console.warn('Access forbidden - insufficient permissions');
    }
    
    // Handle network errors
    if (!error.response) {
      console.error('Network error - no response received');
    }
    
    return Promise.reject(error);
  }
);

export default apiClient; 
